import { DakenService, JessicaJonesService } from '@/hub-shared'
import ErrorMessage from '@enums/error-messages.enum'
import localForageHelper from '@helpers/localforage.helper'
import ToastHelper from '@helpers/toast.helper'
import UserHelper from '@helpers/user.helper'
import useSocialNetworkInformationActions, {
  ActionsAvailables,
} from '@hooks/use-social-network-information-actions/use-social-network-information-actions.hook'
import Category from '@interfaces/category.interface'
import Environment from '@interfaces/environment.interface'
import {
  ErrorHubCore,
  ModalStatusTypes,
  ProfileResponse,
  SocialNetworks,
  SocialNetworksAvailables,
  SocialNetworksResponse,
} from '@interfaces/socialnetworks.interface'
import User, { FacebookDiscoveryUser, Page } from '@interfaces/user.interface'
import onEnvironmentChange from '@observables/environment.observable'
import onUserChange from '@observables/user.observable'
import LogService from '@services/log/log.service'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Subscription } from 'rxjs'
import { take } from 'rxjs/operators'

const MIN_QTD_FIELDS_VALID = 3

interface IGetProfiles {
  action?: ActionsAvailables
  network?: SocialNetworksAvailables
  hash?: string
  idProfile?: string
}

interface IExecuteAction {
  action: ActionsAvailables
  profile: ProfileResponse
  network: SocialNetworksAvailables
}

interface IAction {
  action: ActionsAvailables
  network: SocialNetworksAvailables
  idProfile: string
}

interface Props {
  socialNetworkSelected?: SocialNetworksAvailables
}

export interface IError {
  code?: string
  message: string
  name?: string
  statusCode?: number
}

interface State {
  authUrl?: string
  loading?: boolean
  modalStatus?: ModalStatusTypes
  error?: IError
  idProfile?: string
  pages?: Page[]
  user?: User
  open?: boolean
  stepActive?: number
  socialNetwork?: SocialNetworksAvailables
  categories?: Category[]
  subCategories?: Category[]
  selectedProfile?: string
  newUser?: User
  userToAddOnFinish?: SocialNetworksResponse
  openOnInit?: boolean
}

export default ({ socialNetworkSelected = null }: Props) => {
  const userChange = useRef<Subscription>(undefined)
  const environmentChange = useRef<Subscription>(undefined)
  const authUrl = useRef(undefined)
  const toast = useMemo(() => new ToastHelper().toast, [])
  const userHelper = useMemo(() => new UserHelper(), [])
  const jessica = useMemo(() => new JessicaJonesService(), [])
  const daken = useMemo(() => new DakenService(), [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('useLinkSocialNetwork Hook')

  const { t } = useTranslation('useLinkSocialNetworkHook')

  const [socialNetworkInformationActions, setSocialNetworkInformationActions] = useSocialNetworkInformationActions()

  const initialState: State = useMemo(
    () => ({
      authUrl: '',
      loading: false,
      modalStatus: 'initial',
      error: null,
      idProfile: null,
      pages: [],
      user: null,
      open: false,
      stepActive: 0,
      socialNetwork: socialNetworkSelected === 'instagram' ? 'facebook' : socialNetworkSelected,
      categories: [],
      subCategories: [],
      selectedProfile: null,
      newUser: null,
      username: '',
      openOnInit: false,
      userToAddOnFinish: null,
    }),
    [socialNetworkSelected]
  )

  const [state, setState] = useState<State>(initialState as State)

  const hasInfo = useMemo(() => {
    if (socialNetworkInformationActions) {
      return Object.values(socialNetworkInformationActions)?.reduce((acc, field) => (field ? acc + 1 : acc), 0) >= MIN_QTD_FIELDS_VALID
    }
  }, [socialNetworkInformationActions])

  const onCloseModal = useCallback(async (): Promise<void> => {
    const newState: State = initialState as State
    const { action } = ((await localForageHelper.getItem('actions')) as IAction | null) ?? {}
    if (action === 'renew') {
      await localForageHelper.removeItem('actions')
    }
    setState((prevState) => ({ ...prevState, ...newState }))
  }, [initialState])

  const back = useCallback((): void => {
    const newState: State = {}
    if (state?.stepActive > 0) {
      newState.stepActive = state?.stepActive - 1
    }
    if (state?.stepActive === 0) {
      newState.modalStatus = 'initial'
      newState.socialNetwork = null
    }

    setState((prevState) => ({ ...prevState, ...newState }))
  }, [state?.stepActive])

  const setOpen = useCallback((open: boolean): void => {
    setState((prevState) => ({ ...prevState, open: !open }))
  }, [])

  const getErrorMessage = useCallback(
    (err: ErrorHubCore = null): string => {
      let messageError = t('errors.FailedRequestError')
      const isAPIError = err?.statusCode
      /**
       * * O valor da chave message chega no seguinte formato: "TT001: Acesso Negado"
       * * Precisamos fazer uma verificação somente do código do início, no caso do exemplo, precisamos isolar o TT001
       * * Por isso utilizamos o split para separar a string por ":" e pegamos a primeira parte
       */
      if (isAPIError || ErrorMessage[err?.code]) {
        const statusMessage = err?.code

        /**
         * * O errorInMessageResponse é obtido através de uma comparação feita entre a RegExp de cada erro esperado e o código do erro que acabamos de isolar.
         * * Caso algum erro previsto seja encontrado, a chave referente ao erro é retornada.
         */
        const errorInMessageResponse = Object.keys(ErrorMessage)?.find((error) => {
          const rgxCheck = new RegExp(error, 'i')
          return rgxCheck?.test(statusMessage) && /(FB|TT|TK|YT|HC)/.test(statusMessage)
        })

        // * Caso algum erro esperado tenha sido encontrado
        if (errorInMessageResponse) {
          const messageCodeI18n = ErrorMessage[errorInMessageResponse]

          // * A mensagem de erro só é alterada se existir a chave dentro do arquivo de tradução e seja diferente do valor default 'NO_MESSAGE'
          if (messageCodeI18n && t(`errors.${messageCodeI18n}`, 'NO_MESSAGE') !== 'NO_MESSAGE') {
            messageError = t(`errors.${messageCodeI18n}`)
          }

          // * Altera a mensagem de erro para caso o erro seja esperado
        } else if (ErrorMessage[err?.code] && t(`errors.${err.code}`, 'NO_MESSAGE') !== 'NO_MESSAGE') {
          messageError = t(`errors.${err?.code}`)
        } else if (t(`errors.${statusMessage}`, 'NO_MESSAGE') !== 'NO_MESSAGE') {
          // * Não é um erro esperado pelo facebook
          messageError = t(`errors.${statusMessage}`)
        }
      } else if (state?.error?.message === 'no-fb-pages') {
        return t('errors.notBusiness.0')
      } else if (state?.error?.message === 'no-fb-pages-linked') {
        return t('errors.alreadyLinked')
      }
      return messageError
    },
    [t, state?.error]
  )

  const addAccount = useCallback(
    async ({ profile, network }: { profile: ProfileResponse; network: SocialNetworksAvailables }): Promise<void> => {
      const newState: State = {}
      let listProfiles

      if (['facebook', 'instagram'].includes(network)) {
        const selectedBusinessProfile = (await localForageHelper.getItem('businessProfileSelected')) as FacebookDiscoveryUser
        listProfiles = profile?.users
          ?.filter((page) => {
            const checkUserAlreadyExists = state?.user?.identities?.some((u) => u?.user_id === page?.instagram?.user_id?.toString())
            return !checkUserAlreadyExists
          })
          .sort((a) => (a?.instagram?.user_id === selectedBusinessProfile?.id ? -1 : 1))
      } else {
        listProfiles = profile?.users?.map((u) => ({ ...u, user_id: u?.id }))
      }

      if (profile?.users?.length === 0) {
        newState.modalStatus = 'error'
        newState.error = { message: 'no-fb-pages' }
      } else if (listProfiles?.length === 0) {
        newState.modalStatus = 'error'
        newState.error = { message: 'no-fb-pages-linked' }
      } else {
        newState.modalStatus = 'profile-category'
        newState.pages = listProfiles
      }

      newState.socialNetwork = network

      setState((prevState) => ({ ...prevState, ...newState, newUser: profile?.newUser }))

      if (newState?.error) {
        let messageError
        if (newState?.error?.message === 'no-fb-pages-linked') {
          messageError = t('errors.alreadyLinked')
        }
        logService.error({ message: 'Failed to link profile', user: state?.user, error: messageError ?? newState?.error })
        await localForageHelper.removeItem('actions')
        setState((prevState) => ({ ...prevState, loading: false, modalStatus: 'error', error: newState?.error }))
      }
    },
    [logService, state?.user, t]
  )

  const finishVinculate = useCallback(async (): Promise<void> => {
    if (['facebook', 'instagram'].includes(state?.socialNetwork)) {
      const socialNetworkObject = new SocialNetworks(state?.socialNetwork)
      const urlSearchParams = new window.URLSearchParams(socialNetworkInformationActions?.socialHash)
      const hash = urlSearchParams.get('code')
      setState((prevState) => ({ ...prevState, loading: true }))
      await localForageHelper.removeItem('businessProfileSelected')
      await localForageHelper.removeItem('isSkippedTutorial')
      try {
        await socialNetworkObject.addSocialNetwork(hash, state?.selectedProfile)
      } catch (error) {
        logService.error({ message: 'Failed to link profile', user: state?.user, error: error })
        await localForageHelper.removeItem('actions')
        setState((prevState) => ({ ...prevState, loading: false, modalStatus: 'error', error: newUser?.error }))
      }
      let newUser
      const refreshToken = await jessica?.refreshToken()
      const userInfo = await daken?.getInfluencersInfo(refreshToken?.idToken)

      if (refreshToken?.idToken && refreshToken?.expiresIn) {
        newUser = {
          userInfo: { ...userInfo },
          JWT: refreshToken?.idToken,
          bindedProfileInfo: state?.selectedProfile,
          statusProviders: userInfo?.statusProviders,
          expireDate: refreshToken?.expiresIn,
        }
      }

      setState((prevState) => ({ ...prevState, loading: false, modalStatus: 'success', userToAddOnFinish: newUser }))
      await localForageHelper.removeItem('actions')
    } else {
      setSocialNetworkInformationActions({})
      setState((prevState) => ({ ...prevState, loading: false, modalStatus: 'success' }))
      await localForageHelper.removeItem('actions')
    }
  }, [
    state?.user,
    state?.socialNetwork,
    state?.selectedProfile,
    logService,
    daken,
    jessica,
    setSocialNetworkInformationActions,
    socialNetworkInformationActions?.socialHash,
  ])

  const nextStep = useCallback(async (): Promise<void> => {
    const newState: State = {}

    newState.stepActive = state?.stepActive + 1
    if (newState?.stepActive === 1) {
      return await finishVinculate()
    }
    setState((prevState) => ({ ...prevState, ...newState }))
  }, [finishVinculate, state?.stepActive])

  const getProfiles = useCallback(async ({ action, network, hash, idProfile }: IGetProfiles): Promise<ProfileResponse> => {
    if (!action) return
    const socialNetworkObject = new SocialNetworks(network)
    switch (action) {
      case 'renew':
        return await socialNetworkObject.renewToken(hash, idProfile)
      case 'addAccount':
        return await socialNetworkObject.getSocialNetworkInfo(hash)
      default:
        break
    }
  }, [])

  const onAddAccount = useCallback(async (socialNetwork: SocialNetworksAvailables): Promise<void> => {
    // * Monta url de destino e redireciona
    await localForageHelper.setItem('actions', { action: 'addAccount', idProfile: socialNetwork })
    window.location.href = `${authUrl.current}/${socialNetwork}?squid-callback=${window.location.href}`
  }, [])

  const renewToken = useCallback(
    async ({ profile }: { profile: ProfileResponse }): Promise<void> => {
      await onCloseModal()
      toast.success(t('renewed'), {
        closeButton: true,
        className: 'full text-bold',
        position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
        notOverClick: true,
        immediately: true,
      })
      await userHelper.setUserObject(profile?.userInfo as unknown as User, profile?.userInfo?.JWT, profile?.userInfo?.expireDate)
      await userHelper.setUser({ ...profile?.user, picture: profile?.user?.identities?.[0]?.picture })
    },
    [userHelper, onCloseModal, t, toast]
  )

  const executeAction = useCallback(
    async ({ action, profile, network }: IExecuteAction): Promise<void> => {
      if (profile?.success) {
        if (!state?.newUser) {
          setState((prevState) => ({ ...prevState, newUser: profile?.newUser }))
        }

        if (action === 'addAccount') {
          addAccount({ profile: profile as ProfileResponse, network })
        }
        if (action === 'renew') {
          renewToken({ profile: profile as ProfileResponse })
        }
      } else {
        if (['facebook', 'instagram'].includes(network)) {
          logService.error({ message: 'Failed to link profile', user: state?.user, error: profile?.error })
          await localForageHelper.removeItem('actions')
          setState((prevState) => ({ ...prevState, loading: false, modalStatus: 'error', error: profile?.error }))
        } else {
          const messageError = getErrorMessage(profile?.error)
          logService.error({ message: 'Failed to link profile', user: state?.user, error: profile?.error })
          await onCloseModal()
          await localForageHelper.removeItem('actions')
          toast.error(messageError, {
            closeButton: true,
            className: 'full',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 3600000,
            notOverClick: true,
            immediately: true,
          })
        }
      }
    },
    [addAccount, logService, renewToken, state?.user, state?.newUser, getErrorMessage, onCloseModal, toast]
  )

  const handleAction = useCallback(async (): Promise<void> => {
    const actions: IAction = await localForageHelper.getItem('actions')
    await localForageHelper.removeItem('actions')
    const { socialHash } = socialNetworkInformationActions

    if (!actions?.action || !socialHash) return

    setState((prevState) => ({ ...prevState, loading: true }))

    const regex = /[?&]code=([^&#]*)/
    const hash = socialHash.match(regex)?.[1]

    const urlSearchParams = new URLSearchParams(socialHash)
    const network = urlSearchParams.get('network') as SocialNetworksAvailables

    setState((prevState) => ({ ...prevState, open: true }))

    const mergedProfile = await getProfiles({ action: actions?.action, network, hash, idProfile: actions?.idProfile })

    setState((prevState) => ({ ...prevState, loading: false, profiles: mergedProfile, socialNetwork: network }))

    executeAction({ action: actions?.action, profile: mergedProfile, network })
  }, [socialNetworkInformationActions, executeAction, getProfiles])

  useEffect(() => {
    environmentChange.current = onEnvironmentChange.pipe(take(1)).subscribe((environment: Environment) => {
      authUrl.current = environment?.squidApis?.proxy
    })

    userChange.current = onUserChange.subscribe((userChanged: User) => {
      const newState: State = {}
      newState.user = userChanged
      newState.open = !userChanged?.identities?.length && state?.openOnInit
      setState((prevState) => ({ ...prevState, ...newState }))
    })

    return () => {
      environmentChange.current.unsubscribe()
      userChange.current.unsubscribe()
    }
  }, [state?.openOnInit])

  useEffect(() => {
    if (socialNetworkInformationActions?.socialHash && state?.user) {
      handleAction()
    }
  }, [socialNetworkInformationActions?.socialHash, state?.user, handleAction])

  return {
    hasInfo,
    open: state?.open,
    setOpen,
    setSocialNetworkSelected: (socialNetwork: SocialNetworksAvailables) => setState((prevState) => ({ ...prevState, socialNetwork })),
    state,
    setState,
    onCloseModal,
    back,
    onAddAccount,
    nextStep,
    userToAddOnFinish: state?.userToAddOnFinish,
  }
}
